// ---------------------------------------------------------------------------
// runtimeTokens — aplica no `<html>` os tokens de um tema que veio do painel.
//
// Blueprint: `docs/systems/admin-themes.md` §1 e §6.
//
// ## Por que estilo INLINE, e não uma tag `<style>` injetada
//
// Um tema criado no admin não tem bloco `[data-theme="x"]` no bundle, porque
// CSS é compilado no build. Estilo inline no elemento raiz vence QUALQUER
// seletor, então os tokens do Firestore sobrepõem a cascata inteira sem que um
// único componente mude: eles já leem `var(--surface-base)` e continuam lendo.
//
// Uma `<style>` injetada também funcionaria, mas exigiria montar texto CSS e
// disputar especificidade com os 26 blocos existentes. Inline não disputa.
//
// ## 🔴 A limpeza é a metade que não pode faltar
//
// Token inline sobrevive à troca de tema: sem remover os do tema anterior, quem
// sair de um tema do painel para o Linho levaria junto o papel do outro, e o
// app ficaria numa mistura que não existe em lugar nenhum do catálogo. Por isso
// este módulo LEMBRA o que aplicou — e limpa pelo que aplicou, não por uma
// lista escrita à mão que envelhece.
// ---------------------------------------------------------------------------

/** Nomes que aceitamos aplicar. Custom property, minúsculas, sem truque. */
const NOME_VALIDO = /^--[a-z0-9-]{1,48}$/;

/**
 * Valor plausível de token.
 *
 * ⚠️ O conteúdo pode vir do `localStorage`, que qualquer script da página
 * escreve. Isto **não é fronteira de segurança** (o pior caso é uma cor feia:
 * `setProperty` já recusa valor que não parseia, e custom property não executa
 * nada), mas recusar lixo evita que uma string enorme ou com `;` entre no
 * atributo de estilo e atrapalhe a depuração de quem for olhar o DOM.
 */
const VALOR_VALIDO = /^[^;{}<>]{1,120}$/;

/** Teto de tokens por tema. Os 26 do código usam 46; 80 é folga com limite. */
const MAX_TOKENS = 80;

/** O que este módulo aplicou por último, para saber o que limpar. */
let aplicados: string[] = [];

/**
 * Filtra um mapa de tokens para o que é seguro e faz sentido aplicar.
 *
 * Exportada para o teste e para o painel poderem usar a MESMA regra que a
 * aplicação usa — validar diferente na tela e no runtime é como a vitrine e o
 * servidor divergem de preço.
 */
export function sanitizeTokens(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  let n = 0;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (n >= MAX_TOKENS) break;
    if (typeof v !== "string") continue;
    if (!NOME_VALIDO.test(k) || !VALOR_VALIDO.test(v.trim())) continue;
    out[k] = v.trim();
    n++;
  }
  return out;
}

/**
 * Aplica (ou remove) os tokens de runtime no elemento raiz.
 *
 * `null` limpa tudo o que este módulo tinha aplicado — é o caminho de voltar
 * para um tema do código.
 */
export function applyRuntimeTokens(tokens: Record<string, string> | null): void {
  if (typeof document === "undefined") return;
  const raiz = document.documentElement;

  // Limpa PRIMEIRO, e sempre. Aplicar por cima deixaria órfão todo token que o
  // tema novo não define — e é exatamente aí que mora a mistura impossível.
  for (const nome of aplicados) raiz.style.removeProperty(nome);
  aplicados = [];

  if (!tokens) return;
  const limpos = sanitizeTokens(tokens);
  for (const [nome, valor] of Object.entries(limpos)) {
    raiz.style.setProperty(nome, valor);
    aplicados.push(nome);
  }
}

/** Só para teste: o que está aplicado agora. */
export function appliedTokenNames(): readonly string[] {
  return [...aplicados];
}
